import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  ConvertQuoteGroup,
  useConvertToPriceQuoteSelection,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/hooks/use-convert-to-price-quote-selection';
import { ConvertToPriceQuoteModal } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/convert-to-price-quote-modal';
import { useCreatePriceQuoteFromOrderGuideMutator } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/mutators/use-create-price-quote-from-order-guide-mutator';
import { useEditOrderGuideState } from '@/experiences/sales-portal/domain/order-guides/blocs/contexts/edit-order-guide-context';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

interface ConvertToPriceQuoteModalContainerProps {
  accountName: string;
  chatUUID: string;
  isOpen: boolean;
  onClose: () => void;
  onConverted: (priceQuoteId: string) => void;
}

export const ConvertToPriceQuoteModalContainer: React.FC<
  ConvertToPriceQuoteModalContainerProps
> = ({ accountName, chatUUID, isOpen, onClose, onConverted }) => {
  const { partitions } = useEditOrderGuideState();

  const groups = useMemo<ConvertQuoteGroup[]>(
    () =>
      partitions
        .map((partition) => ({
          name:
            partition.name ??
            i18n.t('sales_portal.order_guides_list.groups.default'),
          packs: partition.itemGroups.flatMap((group) => group.items),
          partitionUUID: partition.partitionUUID,
        }))
        .filter((group) => group.packs.length > 0),
    [partitions],
  );

  const {
    groupViews,
    isPackSelected,
    selectedVariantPackUUIDs,
    toggleGroup,
    toggleGroupExpanded,
    togglePack,
    totalSelected,
  } = useConvertToPriceQuoteSelection(groups);

  const { showErrorToast } = useExperienceToasts();
  const { createPriceQuote, loading: isConverting } =
    useCreatePriceQuoteFromOrderGuideMutator({ onError: showErrorToast });

  const handleConvert = async () => {
    const priceQuoteId = await createPriceQuote({
      chatUUID,
      variantPackUUIDs: selectedVariantPackUUIDs,
    });
    if (priceQuoteId) {
      onConverted(priceQuoteId);
    }
  };

  return (
    <ConvertToPriceQuoteModal
      accountName={accountName}
      groupViews={groupViews}
      isConverting={isConverting}
      isOpen={isOpen}
      isPackSelected={isPackSelected}
      onClose={onClose}
      onConvert={() => void handleConvert()}
      onToggleGroup={toggleGroup}
      onToggleGroupExpanded={toggleGroupExpanded}
      onTogglePack={togglePack}
      totalSelected={totalSelected}
    />
  );
};
